"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DISPOSITIONS = exports.API_DATE_FORMAT = void 0;
exports.StackedBarChart = StackedBarChart;
var _lib = require("@a1s/lib");
var _group = require("@visx/group");
var _scale = require("@visx/scale");
var _shape = require("@visx/shape");
var _d3TimeFormat = require("d3-time-format");
var _react = _interopRequireDefault(require("react"));
var _base = require("../../base");
var _components = require("../../components");
var _animations = require("../../shared/animations");
var _styled = require("./styled");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const API_DATE_FORMAT = '%Y-%m-%d';
exports.API_DATE_FORMAT = API_DATE_FORMAT;
const DISPOSITIONS = ['BULK', 'SPAM', 'SPOOF', 'SUSPICIOUS', 'MALICIOUS-BEC', 'MALICIOUS'];
//
// Types
// -------------------------------------------------------------------------------------------------

// eslint-disable-next-line prettier/prettier

//
// Main component
// -------------------------------------------------------------------------------------------------
exports.DISPOSITIONS = DISPOSITIONS;
/**
 * StackedBarChart
 *
 * @alpha
 */
function StackedBarChart({
  axisBottom = false,
  data,
  height = 280,
  labelHeight = 40,
  margin,
  testId,
  width = 280,
  LabelDisplay = DateDisplay
}) {
  // Temporary fix until EN-14636 is complete
  data = data.map(bar => {
    const entries = Object.entries(bar).map(bar => {
      bar[0] = bar[0] === 'date' ? bar[0] : bar[0].toUpperCase();
      return bar;
    });
    return Object.fromEntries(entries);
  }).map(({
    date,
    MALICIOUS = 0,
    'MALICIOUS-BEC': MALICIOUS_BEC = 0,
    BULK = 0,
    SUSPICIOUS = 0,
    SPOOF = 0,
    SPAM = 0
  }) => {
    return {
      date,
      BULK,
      SPAM,
      SPOOF,
      SUSPICIOUS,
      'MALICIOUS-BEC': MALICIOUS_BEC,
      MALICIOUS
    };
  });
  if (data.length === 0) return null;
  if (!margin) margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };

  // Accept numbers or strings for the dimension props
  height = (0, _lib.num)(height);
  width = (0, _lib.num)(width);
  const colorBulk = 'teal400';
  const colorEmpty = 'gray100';
  const colorSpam = 'teal650';
  const colorSpoof = 'pink300';
  const colorSuspicious = 'pink400';
  const colorLabels = 'gray500';
  const colorLines = 'gray400';
  const colorMaliciousBec = 'pink450';
  const colorMalicious = 'pink500';
  const keys = [...DISPOSITIONS];
  const totals = data.reduce((totals, row) => {
    const total = keys.reduce((dailyTotal, k) => {
      dailyTotal += Number(row[k]);
      return dailyTotal;
    }, 0);
    totals.push(total);
    return totals;
  }, []);
  const parseDate = (0, _d3TimeFormat.timeParse)(API_DATE_FORMAT);
  const format = (0, _d3TimeFormat.timeFormat)('%b %d');
  const formatDate = date => format(parseDate(String(date)));

  // accessors
  const getDate = d => d.date;

  // scales
  const xScale = (0, _scale.scaleBand)({
    domain: data.map(getDate)
  });
  const yScale = (0, _scale.scaleLinear)({
    domain: [0, Math.max(...totals)],
    nice: true
  });
  const colorScale = (0, _scale.scaleOrdinal)({
    domain: keys,
    // The order these appear in is relevant
    range: [colorBulk, colorSpam, colorSpoof, colorSuspicious, colorMaliciousBec, colorMalicious]
  });
  const xMax = width;
  const yMax = height - margin.top - (axisBottom ? 100 : 0);
  const groups = {
    'MALICIOUS-BEC': 0,
    MALICIOUS: 0,
    SUSPICIOUS: 0,
    SPOOF: 0,
    SPAM: 1,
    BULK: 2
  };

  // Gap between bars
  const verticalGap = data.length > 7 ? 1 : 3;
  xScale.range([0, xMax]);
  yScale.range([yMax, keys.length * verticalGap]);

  // Radius of bar borders
  const borderRadius = data.length > 60 ? 0 : data.length >= 30 ? 1 : 2;

  // Gap between line and connecting bar
  const lineGap = 2;

  // Total height of lines connecting bars to labels
  const lineLength = 10;
  const approxMaxBars = 10;
  const barHorizontalGap = data.length > 60 ? 1 : data.length >= 30 ? 1 : 3;
  const totalLabelHeight = lineLength * 1.5 + lineGap;
  return /*#__PURE__*/_react.default.createElement(_styled.BaseContainer, {
    "data-testid": testId
  }, /*#__PURE__*/_react.default.createElement(_styled.StyledSVG, {
    viewBox: `0 0 ${width + labelHeight} ${height + labelHeight + totalLabelHeight}`
  }, /*#__PURE__*/_react.default.createElement(_group.Group, {
    top: margin.top
  }, /*#__PURE__*/_react.default.createElement(_shape.BarStack, {
    color: colorScale,
    data: data,
    keys: keys,
    x: getDate,
    xScale: xScale,
    yScale: yScale
  }, barStacks => {
    var _lines, _lines2;
    const allBars = barStacks.map(bar => bar.bars).flat();
    const normalBars = allBars.map(bar => {
      return {
        ...bar,
        type: 'FilledBar'
      };
    });
    const dates = new Set();
    const lines = allBars.reduce((prev = [], {
      bar: {
        data: {
          date
        }
      } = {
        data: {
          date: ''
        }
      },
      width,
      x
    }, index) => {
      const skipBars = Math.ceil(data.length / approxMaxBars);
      if (!dates.has(date) && (data.length <= 7 || index === data.length - 1 || index % skipBars === 0)) {
        prev.push({
          color: colorLines,
          height: labelHeight,
          index,
          key: date,
          label: date,
          type: 'DateLabel',
          width: width,
          x: x,
          y: parseInt(height.toString())
        });
      }
      dates.add(date);
      return prev;
    }, []);

    // When the last label gets too close to the second to last, we should remove it so it doesn't overlap
    if (data.length > 0 && ((_lines = lines[lines.length - 1]) === null || _lines === void 0 ? void 0 : _lines.x) - ((_lines2 = lines[lines.length - 2]) === null || _lines2 === void 0 ? void 0 : _lines2.x) <= lineLength) {
      lines.splice(lines.length - 2, 1);
    }
    const grayBars = allBars.reduce((prev = [], curr) => {
      prev[curr.index] = prev[curr.index] ? {
        ...prev[curr.index],
        type: 'EmptyBar',
        height: prev[curr.index].height + curr.height
      } : {
        width: curr.width,
        height: curr.height,
        key: '_',
        type: 'EmptyBar',
        index: curr.index,
        color: colorEmpty,
        x: curr.x,
        y: 0
      };
      return prev;
    }, []).map(bar => {
      bar.height = parseInt(height.toString()) - bar.height;
      return bar;
    });
    return [...lines, ...[...normalBars, ...grayBars].reduce((prev = [], curr) => {
      prev[curr.index] = prev[curr.index] || [];
      prev[curr.index].push(curr);
      return prev;
    }, []).map(column => {
      return column.reduce((prev = [], curr) => {
        const i = groups[curr.key];
        if (i !== undefined) {
          prev[i] = prev[i] || [];
          prev[i].push(curr);
        } else {
          prev[column.length + prev.length] = [curr];
        }
        return prev;
      }, []).filter(column => column !== undefined);
    }).map(column => {
      return column.sort((aBar, bBar) => {
        return aBar[0].y - bBar[0].y;
      }).map((group, groupIndex) => {
        return group.sort((aBar, bBar) => aBar.y - bBar.y).map((bar, barIndex) => {
          const maxGap = verticalGap * (column.length - 2);
          const gap = maxGap - verticalGap * (groupIndex - 1);
          if (bar.color === colorEmpty) {
            // Gray Bar
            bar.gap = height == bar.height ? 0 : maxGap;
            bar.borders = [borderRadius, borderRadius, 0, 0];
          } else if (group[barIndex - 1] == undefined && group[barIndex + 1] !== undefined) {
            // Top of group
            bar.gap = gap;
            bar.borders = [borderRadius, borderRadius, 0, 0];
          } else if (group[barIndex - 1] !== undefined && group[barIndex + 1] === undefined) {
            // Bottom of Group
            bar.gap = gap;
            bar.borders = [0, 0, borderRadius, borderRadius];
            if (bar.height < borderRadius) {
              bar.borders = [0, 0, 0, 0];
            }
          } else if (group.length === 1) {
            // Orphan Bar
            bar.gap = gap;
            bar.borders = [borderRadius, borderRadius, borderRadius, borderRadius];
            if (bar.height < borderRadius) {
              bar.borders = [0, 0, 0, 0];
            }
          } else {
            // Other or center bar
            bar.gap = gap;
            bar.borders = [0, 0, 0, 0];
          }
          return bar;
        });
      });
    }).flat().flat()].map(bar => {
      bar.width = bar.width - barHorizontalGap;
      bar.x = bar.x + barHorizontalGap / 2;
      return bar;
    }).map(bar => {
      const {
        borders = [0, 0, 0, 0],
        color,
        gap = 0,
        height,
        key,
        label = '',
        type,
        width,
        x,
        y = 0
      } = bar;
      const labelDate = parseDate(label);
      if (type == 'DateLabel') {
        return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, {
          key: key
        }, /*#__PURE__*/_react.default.createElement("g", {
          transform: `translate(${x + width / 2}, ${y + totalLabelHeight}) rotate(45)`
        }, /*#__PURE__*/_react.default.createElement(_styled.StyledLabel, {
          fill: colorLabels
        }, /*#__PURE__*/_react.default.createElement(LabelDisplay, {
          label: label,
          value: labelDate
        }))), /*#__PURE__*/_react.default.createElement(_styled.StyledLine, {
          d: `M ${x + width / 2} ${y + lineGap} l 0 ${lineLength / 2} l ${lineLength / 2} ${lineLength / 2}`
        }));
      }
      const path = [
      // Starting Position
      `M ${bar.x + borders[0]},${bar.y - gap}`,
      // Top Side (Left to Right)
      `l ${width - (borders[1] + borders[0])}, 0`,
      // Top Right Radius
      borders[1] > 0 ? `a ${borders[1]}, ${borders[1]} 0 0 1 ${borders[1]}, ${borders[1]}` : undefined,
      // Right Side (Top to Bottom)
      `l 0, ${height - (borders[1] + borders[2])}`,
      // Bottom Right Radius
      borders[2] > 0 ? `a ${borders[2]}, ${borders[2]} 0 0 1 -${borders[2]}, ${borders[2]}` : undefined,
      // Bottom Side (Right to Left)
      `l -${width - (borders[2] + borders[3])}, 0`,
      // Bottom Left Radius
      borders[3] > 0 ? `a ${borders[3]}, ${borders[3]} 0 0 1 -${borders[3]}, -${borders[3]}` : undefined,
      // Left Side (Bottom to Top)
      `l 0, -${height - (borders[3] + borders[0])}`,
      // Top Left Radius
      borders[0] > 0 ? `a ${borders[0]}, ${borders[0]} 0 0 1 ${borders[0]}, -${borders[0]}` : undefined];
      if (type === 'EmptyBar') {
        return /*#__PURE__*/_react.default.createElement(_styled.StyledPath, {
          fill: color,
          d: path.join(' '),
          key: `bar-stack-bg-${bar.key}-${bar.index}`
        });
      } else if (type === 'FilledBar') {
        var _bar$bar, _bar$bar2;
        if ((_bar$bar = bar.bar) !== null && _bar$bar !== void 0 && _bar$bar.data && bar.bar.data[key] == 0) {
          return null;
        }
        return /*#__PURE__*/_react.default.createElement(_styled.StyledPath, {
          d: path.join(' '),
          fill: color,
          key: `bar-stack-bg-${bar.key}-${bar.index}`
        }, /*#__PURE__*/_react.default.createElement("title", null, ((_bar$bar2 = bar.bar) === null || _bar$bar2 === void 0 ? void 0 : _bar$bar2.data) && bar.bar.data[key].toLocaleString(), " ", key, " -", ' ', bar.bar && formatDate(getDate(bar.bar.data))));
      }
    });
  }))));
}

//
// Utility components
// -------------------------------------------------------------------------------------------------

StackedBarChart.Loadable = Loadable;
function Loadable({
  data,
  height,
  loading,
  width,
  ...props
}) {
  const isLoading = (0, _components.useLoadable)(loading);
  if (!isLoading && data) return /*#__PURE__*/_react.default.createElement(StackedBarChart, _extends({
    data: data,
    height: height,
    width: width
  }, props));
  return /*#__PURE__*/_react.default.createElement(_base.Box, {
    bg: "$gray300",
    css: {
      animation: `${_animations.pulse} 1.5s linear infinite`,
      height: `${height}px`,
      width: `${width}px`
    },
    r: true
  });
}
function DateDisplay({
  label,
  value
}) {
  const today = (0, _d3TimeFormat.timeFormat)(API_DATE_FORMAT)(new Date());
  if (label === today) return /*#__PURE__*/_react.default.createElement("tspan", null, "\xA0TODAY");
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_styled.StyledMonthLabel, {
    style: {
      fontWeight: 'bold'
    }
  }, (0, _d3TimeFormat.timeFormat)('%b')(value).toUpperCase()), /*#__PURE__*/_react.default.createElement("tspan", null, " ", (0, _d3TimeFormat.timeFormat)('%d')(value)));
}